import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePendingApprovals, useApproveReview, useRejectReview } from '@saiens/shared/hooks';
import { formatWorkHours } from '@saiens/shared/utils';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { ArrowLeft, Check, X } from 'lucide-react';
import type { ReviewItem } from '@saiens/shared/types';

function ReviewCard({
  review,
  onApprove,
  onReject,
  isActing,
}: {
  review: ReviewItem;
  onApprove: (id: number, comment: string) => void;
  onReject: (id: number, comment: string) => void;
  isActing: boolean;
}) {
  const navigate = useNavigate();
  const [showComment, setShowComment] = useState(false);
  const [comment, setComment] = useState('');
  const [actionType, setActionType] = useState<'approve' | 'reject' | null>(null);

  const rd = review.resource_data;

  const handleAction = (type: 'approve' | 'reject') => {
    setActionType(type);
    setShowComment(true);
  };

  const handleConfirm = () => {
    if (actionType === 'approve') {
      onApprove(review.id, comment);
    } else {
      onReject(review.id, comment);
    }
    setShowComment(false);
    setComment('');
    setActionType(null);
  };

  return (
    <div className="rounded-xl bg-white p-4 shadow-sm">
      {/* Type badge + applicant */}
      <div className="mb-2 flex items-center justify-between">
        <span className="rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-medium text-indigo-700">
          {review.model_display}
        </span>
        <button
          onClick={() => navigate(`/approval/${review.id}`)}
          className="text-xs text-blue-500 hover:underline"
        >
          詳情
        </button>
      </div>

      {/* Resource Info */}
      {rd && (
        <div className="mb-3 space-y-1">
          <p className="text-sm font-medium text-gray-900">
            {'employee_name' in rd ? rd.employee_name : ''} - {review.resource_name}
          </p>
          {rd.type === 'leave' && (
            <>
              <p className="text-xs text-gray-500">
                {rd.holiday_type_name} | {rd.start_day}
                {rd.end_day && rd.end_day !== rd.start_day ? ` ~ ${rd.end_day}` : ''}
              </p>
              <p className="text-xs text-gray-500">
                {formatWorkHours(rd.holiday_time_total)} | 代理人: {rd.substitute_name || '-'}
              </p>
              {rd.report_note && (
                <p className="text-xs text-gray-400">事由: {rd.report_note}</p>
              )}
            </>
          )}
          {rd.type === 'overtime' && (
            <>
              <p className="text-xs text-gray-500">
                {rd.overtime_type_name} | {rd.start_day}
              </p>
              <p className="text-xs text-gray-500">
                {formatWorkHours(rd.duration_time)}
              </p>
              {rd.desc && (
                <p className="text-xs text-gray-400">事由: {rd.desc}</p>
              )}
            </>
          )}
        </div>
      )}

      {/* Comment Modal */}
      {showComment && (
        <div className="mb-3 space-y-2">
          <textarea
            value={comment}
            onChange={(e) => setComment(e.target.value)}
            rows={2}
            placeholder="備註（選填）"
            className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-indigo-500 focus:outline-none"
          />
          <div className="flex gap-2">
            <button
              onClick={() => {
                setShowComment(false);
                setActionType(null);
              }}
              className="flex-1 rounded-lg border border-gray-300 py-2 text-xs text-gray-600"
            >
              取消
            </button>
            <button
              onClick={handleConfirm}
              disabled={isActing}
              className={`flex-1 rounded-lg py-2 text-xs font-medium text-white disabled:opacity-40 ${
                actionType === 'approve'
                  ? 'bg-green-500 hover:bg-green-600'
                  : 'bg-red-500 hover:bg-red-600'
              }`}
            >
              確認{actionType === 'approve' ? '核准' : '駁回'}
            </button>
          </div>
        </div>
      )}

      {/* Action Buttons */}
      {!showComment && (
        <div className="flex gap-2">
          <button
            onClick={() => handleAction('reject')}
            disabled={isActing}
            className="flex flex-1 items-center justify-center gap-1 rounded-lg border border-red-200 bg-white py-2 text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-40"
          >
            <X className="h-3.5 w-3.5" />
            駁回
          </button>
          <button
            onClick={() => handleAction('approve')}
            disabled={isActing}
            className="flex flex-1 items-center justify-center gap-1 rounded-lg bg-green-500 py-2 text-xs font-medium text-white hover:bg-green-600 disabled:opacity-40"
          >
            <Check className="h-3.5 w-3.5" />
            核准
          </button>
        </div>
      )}
    </div>
  );
}

export function PendingApprovalsPage() {
  const navigate = useNavigate();
  const { data: pendingList, isLoading } = usePendingApprovals();
  const approveReview = useApproveReview();
  const rejectReview = useRejectReview();

  const isActing = approveReview.isPending || rejectReview.isPending;

  const handleApprove = (id: number, comment: string) => {
    approveReview.mutate({ id, comment });
  };

  const handleReject = (id: number, comment: string) => {
    rejectReview.mutate({ id, comment });
  };

  return (
    <div className="mx-auto max-w-lg">
      {/* Header */}
      <div className="sticky top-0 z-10 flex items-center gap-3 bg-white px-4 py-3 shadow-sm">
        <button onClick={() => navigate(-1)} className="rounded-full p-1 hover:bg-gray-100">
          <ArrowLeft className="h-5 w-5" />
        </button>
        <h1 className="text-lg font-semibold">待辦簽核</h1>
        {pendingList && pendingList.length > 0 && (
          <span className="rounded-full bg-red-500 px-2 py-0.5 text-xs font-semibold text-white">
            {pendingList.length}
          </span>
        )}
      </div>

      <div className="px-4 py-4">
        {isLoading ? (
          <div className="flex h-32 items-center justify-center">
            <LoadingSpinner />
          </div>
        ) : pendingList && pendingList.length > 0 ? (
          <div className="space-y-3">
            {pendingList.map((review) => (
              <ReviewCard
                key={review.id}
                review={review}
                onApprove={handleApprove}
                onReject={handleReject}
                isActing={isActing}
              />
            ))}
          </div>
        ) : (
          <p className="py-8 text-center text-sm text-gray-400">暫無待辦簽核</p>
        )}
      </div>
    </div>
  );
}
